<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page expense-summary-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="统计月份" required>
                <a-month-picker v-model="queryMonth" format="YYYY-MM" placeholder="选择月份" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="关键字">
                <a-input v-model="query.keyword" allow-clear placeholder="项目/费用代码/名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>费用汇总表</h3>
            <p>按费用项目 + 费用代码 + 名称汇总所选月份内已审核费用单明细行（line_role=expense_item）；项目/代码/名称来自行快照 category_name、sku_code、sku_name；金额为行 current_amount。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="displayMonth" class="expense-summary-totals">
              {{ displayMonth }} 合计
              <strong class="erp-money">¥{{ formatMoney(monthGrandTotal) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:expense-summary:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无汇总数据' }"
          @change="onPageChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pageExpenseSummaryReport,
  exportExpenseSummaryReport
} from '@/api/erp/expenseSummaryReport'

const emptyQuery = () => ({
  keyword: ''
})

export default {
  name: 'ErpExpenseSummaryReport',
  data () {
    return {
      query: emptyQuery(),
      queryMonth: moment(),
      displayMonth: '',
      rows: [],
      monthGrandTotal: 0,
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` }
    }
  },
  computed: {
    tableScroll () {
      const w = this.tableColumns.reduce((sum, col) => sum + (col.width || 0), 0)
      return w > 0 ? { x: w } : undefined
    },
    tableColumns () {
      return [
        { title: '项目', dataIndex: 'projectName', width: 160, scopedSlots: { customRender: 'text' } },
        { title: '费用代码', dataIndex: 'expenseCode', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '费用名称', dataIndex: 'expenseName', width: 220, scopedSlots: { customRender: 'text' } },
        { title: '本月合计金额', dataIndex: 'monthTotalAmount', width: 140, scopedSlots: { customRender: 'money' } }
      ]
    }
  },
  created () {
    this.load()
  },
  methods: {
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    expenseMonthParam () {
      if (!this.queryMonth) {
        return undefined
      }
      return moment(this.queryMonth).format('YYYY-MM')
    },
    queryParams () {
      return {
        expenseMonth: this.expenseMonthParam(),
        keyword: (this.query.keyword || '').trim() || undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    load () {
      if (!this.queryMonth) {
        this.$message.warning('请选择统计月份')
        return
      }
      this.loading = true
      pageExpenseSummaryReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = data.records || []
        this.pagination.total = Number(data.total || 0)
        this.monthGrandTotal = Number(data.monthGrandTotal || 0)
        this.displayMonth = data.expenseMonth || this.expenseMonthParam()
      }).catch(() => {
        this.rows = []
        this.pagination.total = 0
        this.monthGrandTotal = 0
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.queryMonth = moment()
      this.pagination.current = 1
      this.load()
    },
    onPageChange (pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.load()
    },
    async doExport () {
      if (!this.queryMonth) {
        this.$message.warning('请选择统计月份')
        return
      }
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportExpenseSummaryReport(params)
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `费用汇总_${this.expenseMonthParam() || moment().format('YYYYMM')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.expense-summary-totals {
  margin-right: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.expense-summary-totals strong {
  margin-left: 4px;
}
</style>
